import { useRef, useState } from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { Input, FormControl } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { subDays } from "date-fns";

import BackButton from "../../../components/BackButton";
import useStorage from "../../../hooks/useStorage";
import { UPDATE_EMPLOYEE } from "../../../graphql/mutations";
import { IUpdateDriver } from "../../../interfaces/driver.interface";
import Spinner from "../../../components/Spinner";
import { GET_COUNT_PER_DAY_EMPLOYEE } from "../../../graphql/querys";

const LineDayStat = ({ day, employeeId }: any) => {
  const heightCountValueItem = 20;

  const { data, loading } = useQuery(GET_COUNT_PER_DAY_EMPLOYEE, {
    variables: {
      employeeId,
      date: dayjs(day).format(),
    },
  });

  const heightColumns =
    data?.minutesPerDayOfDriver === 0
      ? heightCountValueItem
      : data?.minutesPerDayOfDriver === 60
      ? heightCountValueItem * 2
      : (data?.minutesPerDayOfDriver / 60) * heightCountValueItem;

  if (loading) return null;

  return (
    <div className="h-[288px] flex flex-col items-center justify-end">
      <div
        key={day}
        className={`w-9 bg-darkBlue rounded-lg grid place-items-center`}
        style={{ height: heightColumns }}
      >
        <p className="text-sm font-medium text-white">
          {data?.minutesPerDayOfDriver === 0
            ? ""
            : data?.minutesPerDayOfDriver / 60}
        </p>
      </div>

      <p className="mt-1 text-lightBlue font-medium text-sm uppercase">
        {dayjs(day).format("dd")}
      </p>
    </div>
  );
};

export default function EditDriver() {
  const location = useLocation();
  const currentDate = dayjs().startOf('day');

  const prevDays = [currentDate.format()];

  for (let i = 1; i < 7; i++) {
    const prevDay = currentDate.subtract(i, "day").startOf('day');
    prevDays.push(prevDay.format());
  }

  prevDays.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());

  const navigate = useNavigate();
  const refButtonImage = useRef<any>(null);

  const [updateEmployee, { loading }] = useMutation(UPDATE_EMPLOYEE);

  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [uploading, uploadFileToAws] = useStorage();
  const [uploadUrlFile, setUploadUrlFile] = useState(
    location.state.info.picture
  );

  const valuesCountEmployee = prevDays.map((day) => {
    const { data, loading } = useQuery(GET_COUNT_PER_DAY_EMPLOYEE, {
      variables: {
        employeeId: +location.pathname.split("/")[2],
        date: dayjs(day).format(),
      },
    });

    console.log({ data });

    if (loading) return;

    return data?.minutesPerDayOfDriver / 60;
  });

  const valuesCountEmployeeSum = valuesCountEmployee?.reduce(
    (a: any, b: any) => parseFloat(a) + parseFloat(b)
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      id: location.state.info.id,
      name: location.state.info.name,
      picture: location.state.info.picture,
    },
  });

  function onSubmit(values: IUpdateDriver) {
    updateEmployee({
      variables: {
        data: {
          ...values,
        },
      },
    })
      .then((res) => {
        console.log(res.data);
        if (res.data?.updateEmployee.status === 200) {
          const notify = () =>
            toast.success("Driver updated successfully");
          notify();
          return navigate(-1);
        }

        const notify = () =>
          toast("A driver with that name already exists", {
            icon: "⚠️",
          });
        notify();
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const handleFile = (e: any) => {
    const files = e.target.files;

    if (files) {
      const file = files[0];

      uploadFileToAws(file)
        .then((res: any) => {
          console.log("RES ON UPLOAD FILE uploadFileToAws", res);
          setUploadUrlFile(res.url);
          setValue("picture", res.url);
        })
        .catch((err: any) => {
          console.log("ERROR ON UPLOAD FILE uploadFileToAws", err);
        });
    }
  };

  return (
    <div>
      <BackButton goBackRoute="/list-drivers" title="EDIT DRIVER" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col px-5 h-[calc(100vh_-_88px_-_32px)]">
          <div className="h-full">
            <FormControl isInvalid={!!errors.name?.message}>
              <Input
                className="form-control-input"
                type="text"
                placeholder="DRIVER"
                {...register("name", {
                  required: "This is required",
                  maxLength: {
                    value: 16,
                    message: "The maximum number of characters is 16",
                  },
                })}
              />
              <p className="text-danger text-sm mt-2">
                {/* @ts-ignore */}
                {errors.name && errors.name.message}
              </p>
            </FormControl>

            <div className="flex mt-4">
              {uploadUrlFile && (
                <div className="mr-5 w-[120px]">
                  <img
                    className="rounded-md w-full h-[120px] object-cover"
                    src={uploadUrlFile}
                    alt=""
                  />
                </div>
              )}

              <input
                ref={refButtonImage}
                type="file"
                style={{ display: "none" }}
                name="file"
                id="file"
                onChange={handleFile}
              />

              <button
                type="button"
                className="border border-darkBlue text-darkBlue rounded-md w-[130px] h-[120px]"
                onClick={() => refButtonImage.current?.click()}
              >
                {uploadUrlFile ? "Change Image" : "Add Image"}
              </button>
            </div>

            {valuesCountEmployeeSum ||
            valuesCountEmployeeSum === 0 ? (
              <div className="overflow-hidden flex flex-col justify-end py-3">
                <h3 className="text-lg text-darkBlue font-semibold mb-2">
                  Weekly Hours
                </h3>

                <div className="h-auto items-end flex justify-between">
                  {prevDays.map((day) => (
                    <LineDayStat
                      key={day}
                      day={day}
                      employeeId={location.pathname.split("/")[2]}
                    />
                  ))}
                </div>

                <div className="mt-2 flex items-center gap-1 justify-center">
                  <p className="font-bold text-darkBlue">
                    {valuesCountEmployeeSum!}{" "}
                  </p>
                  <span className="text-darkBlue">Total Hours</span>
                </div>
              </div>
            ) : (
              <div className="h-[380px] grid place-items-center">
                <p>Loading..</p>
              </div>
            )}
          </div>

          <button
            type="submit"
            className={`bg-darkBlue h-[54px] text-white rounded-md font-medium text-lg ${
              !getValues().picture ? "opacity-50" : "opacity-100"
            }`}
            disabled={!getValues().picture}
          >
            {!!isSubmitting || !!loading || !!uploading ? (
              <Spinner />
            ) : isCreating ? (
              "Saving.."
            ) : (
              "Save changes"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
